import type { PortalBucketCoverage } from '../../types/portalReport.js';

// ⚠️⚠️ THE TITLE AND THE PART-MONTH FOOTNOTE OF A TITLED CHART CARD (2026-11-12,
// Round 5 §A). PURE: no React, no I/O.
//
// A chart with no title is a chart nobody can place — "which section is this, and
// which metric am I looking at?" — so the automotive pages' cards draw
// "<section> · <selected metric>", and the metric half FOLLOWS the selected chip.
// ⚠️ A blank section THROWS rather than falling back: a card with no title is a
// wiring bug to surface at the first render, not a state to render quietly. That is
// what "a chart card cannot render without a title" means, and it is asserted.

/** "<section> · <metric>". Throws on a blank section or metric. */
export function chartCardTitle(section: string, metricLabel: string): string {
  const s = section.trim();
  const m = metricLabel.trim();
  if (s === '') throw new Error('chartCardTitle: a chart card must name its section');
  if (m === '') throw new Error('chartCardTitle: a chart card must name its metric');
  return `${s} · ${m}`;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** 1 → "1st", 22 → "22nd", 30 → "30th", 11 → "11th". */
export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

function yesterdayOf(today: string): { year: number; month: number; day: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  d.setUTCDate(d.getUTCDate() - 1);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/**
 * The one-line footnote for the hatched part-month bar, or null when the last
 * bucket is not an unfinished month. ⚠️ The data runs to YESTERDAY (today holds no
 * complete figures; the same rule as the report's month-to-date period), so
 * "October runs to the 30th" on the 31st. Months grain only — a daily or weekly
 * bucket is not "a part month".
 */
export function partMonthNote(
  points: ReadonlyArray<{ bucket: string; coverage: PortalBucketCoverage }>,
  grain: 'months' | 'period',
  today: string,
): string | null {
  if (grain !== 'months') return null;
  const last = points[points.length - 1];
  if (!last || last.coverage !== 'in-progress') return null;
  const m = /^(\d{4})-(\d{2})$/.exec(last.bucket);
  if (!m) return null;
  const y = yesterdayOf(today);
  if (!y || y.year !== Number(m[1]) || y.month !== Number(m[2])) return null;
  const name = MONTHS[Number(m[2]) - 1];
  if (!name) return null;
  return `${name} runs to the ${ordinal(y.day)}, so it's a part month.`;
}
